// Assets modals module for Docospot
// Handles create/edit asset modals and CRUD operations

import { t } from '../core/i18n.js';
import { auth } from '../core/auth.js';
import { API_BASE_URL } from '../core/config.js';
import { showError, showSuccess, showConfirmDialog } from '../core/ui.js';
import { loadAssets } from './assets.js';
import { initAccountSearch } from '../utils/account-search.js';

/**
 * Show asset modal (create or edit)
 */
export async function showAssetModal(assetId = null) {
    const modal = document.getElementById('modal');
    if (!modal) return;
    
    let asset = null;
    if (assetId) {
        // Fetch asset details for edit
        try {
            const response = await fetch(`${API_BASE_URL}/assets/${assetId}`, {
                headers: { 'Authorization': `Bearer ${auth.getToken()}` }
            });
            if (!response.ok) throw new Error('Failed to load asset');
            asset = await response.json();
        } catch (error) {
            showError('Failed to load asset: ' + error.message);
            return;
        }
    }
    
    const isEdit = asset !== null;
    const modalTitle = isEdit ? t('assets.editAsset') : t('assets.createAsset');

    modal.innerHTML = `
        <div class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div class="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
                <div class="mt-3">
                    <h3 class="text-lg font-medium text-gray-900 mb-4">${modalTitle}</h3>
                    <form id="asset-form" class="space-y-4">
                        ${isEdit ? `<input type="hidden" id="asset-id" value="${asset._id || asset.id}">` : ''}
                        
                        <!-- Name and Account - Two Column -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <!-- Asset ID -->
                            <div>
                                <label for="asset-asset-id" class="block text-sm font-medium text-gray-700">${t('assets.assetId')} <span class="text-red-500">*</span></label>
                                <input type="text" id="asset-asset-id" name="asset_id" required value="${isEdit ? asset.asset_id || '' : ''}"
                                       class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                       placeholder="${t('assets.assetIdPlaceholder')}">
                                <p class="mt-1 text-xs text-gray-500">${t('assets.assetIdHelp')}</p>
                            </div>
                            
                            <!-- Name -->
                            <div>
                                <label for="asset-name" class="block text-sm font-medium text-gray-700">${t('assets.name')} <span class="text-red-500">*</span></label>
                                <input type="text" id="asset-name" name="name" required value="${isEdit ? asset.name : ''}"
                                       class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                       placeholder="${t('assets.namePlaceholder')}">
                            </div>
                        </div>
                        
                        <div class="grid grid-cols-1 gap-4">
                            <!-- Account -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">${t('assets.account')} <span class="text-red-500">*</span></label>
                                ${isEdit ? `
                                    <!-- Cannot change account when editing -->
                                    <input type="text" value="${asset.account_name || 'Unknown Account'}" disabled
                                           class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm">
                                    <input type="hidden" id="asset-account" name="account_id" value="${asset.account_id}">
                                    <p class="mt-1 text-xs text-gray-500">${t('assets.accountCannotChange')}</p>
                                ` : `
                                    <!-- Search Field -->
                                    <div class="relative mb-2">
                                        <input type="text" 
                                               id="asset-account-search" 
                                               placeholder="${t('assets.searchAccounts')}" 
                                               class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                                        <!-- Search Results Dropdown -->
                                        <div id="asset-account-search-results" 
                                             class="hidden absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-y-auto">
                                        </div>
                                    </div>
                                    <!-- Account Tree -->
                                    <div id="asset-account-tree-container" class="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto bg-white">
                                        <div class="text-gray-400 text-sm">${t('common.loading')}</div>
                                    </div>
                                    <input type="hidden" id="asset-account" name="account_id" required>
                                `}
                            </div>
                        </div>
                        
                        <!-- Home Location -->
                        <div>
                            <label for="asset-home-location" class="block text-sm font-medium text-gray-700">${t('assets.homeLocation')}</label>
                            <select id="asset-home-location" name="home_location_id"
                                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                                <option value="">${t('assets.noHomeLocation')}</option>
                            </select>
                            <p class="mt-1 text-xs text-gray-500">${t('assets.homeLocationHelp')}</p>
                        </div>
                        
                        <!-- Sensors -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">${t('assets.sensors')}</label>
                            <input type="text" id="asset-sensor-search"
                                   placeholder="Search sensors..."
                                   class="w-full mb-2 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500">
                            <div id="asset-sensors-list" class="border border-gray-300 rounded-md p-3 max-h-64 overflow-y-auto bg-white">
                                <div class="text-gray-400 text-sm">${t('assets.selectAccountFirst')}</div>
                            </div>
                            <p class="mt-1 text-xs text-gray-500">${t('assets.sensorsHelp')}</p>
                        </div>
                        
                        <!-- Buttons -->
                        <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                            <button type="button" onclick="closeModal()" 
                                    class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                                ${t('common.cancel')}
                            </button>
                            <button type="submit" id="asset-submit-btn"
                                    class="px-4 py-2 bg-primary-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                                ${isEdit ? t('common.save') : t('common.create')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    `;
    
    modal.classList.remove('hidden');
    
    // Set up form submission
    const form = document.getElementById('asset-form');
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        await saveAsset(isEdit);
    });
    
    // Wire up sensor search filtering (once, at modal open time)
    const sensorSearchInput = document.getElementById('asset-sensor-search');

    // Handler called whenever the user picks a different account
    async function onAccountSelected(accountId) {
        if (!accountId) return;
        await loadLocationsForAccount(accountId);
        await loadSensorsForAccount(accountId);
        // Reset search field when account changes
        if (sensorSearchInput) sensorSearchInput.value = '';
    }

    // Load accounts and set up account search (only for create)
    if (!isEdit) {
        await loadAccountsForAssetModal();
        // Pass onAccountSelected as the callback so selecting from the search dropdown triggers it
        initAccountSearch('asset-account-search', 'asset-account-search-results', 'asset-account', 'asset-account-tree-container', onAccountSelected);

        // Also catch clicks directly on radio buttons in the account tree
        const treeContainer = document.getElementById('asset-account-tree-container');
        if (treeContainer) {
            treeContainer.addEventListener('change', async (e) => {
                if (e.target.type === 'radio') {
                    await onAccountSelected(e.target.value);
                }
            });
        }
    }

    // Load locations and sensors based on selected account (edit mode)
    const accountId = isEdit ? asset.account_id : null;
    if (accountId) {
        await loadLocationsForAccount(accountId, isEdit ? asset.home_location_id : null);
        await loadSensorsForAccount(accountId, isEdit ? asset.sensor_ids : []);
    }
}

/**
 * Load accounts hierarchy for the modal
 */
async function loadAccountsForAssetModal() {
    try {
        const response = await fetch(`${API_BASE_URL}/accounts/hierarchy`, {
            headers: { 'Authorization': `Bearer ${auth.getToken()}` }
        });
        
        if (!response.ok) throw new Error('Failed to load accounts');
        
        const data = await response.json();
        const container = document.getElementById('asset-account-tree-container');
        
        if (data && data.accounts_tree) {
            container.innerHTML = renderAccountTree(data.accounts_tree);
        } else {
            container.innerHTML = `<div class="text-gray-500 text-sm">${t('sensors.noAccountsFound')}</div>`;
        }
    } catch (error) {
        console.error('Error loading accounts:', error);
        const container = document.getElementById('asset-account-tree-container');
        if (container) {
            container.innerHTML = `<div class="text-red-500 text-sm">${t('sensors.errorLoadingAccounts')}</div>`;
        }
    }
}

/**
 * Render account tree for selection
 */
function renderAccountTree(accounts, level = 0) {
    let html = '';
    
    accounts.forEach(account => {
        const accountId = account._id || account.id;
        const accountName = account.account_name || account.name || 'Unnamed Account';
        const hasChildren = account.children && account.children.length > 0;
        const indentPadding = level * 20;
        
        html += `
            <div class="account-tree-item" data-level="${level}">
                <div class="flex items-center py-1.5 hover:bg-gray-50 rounded" style="padding-left: ${indentPadding}px;">
                    ${hasChildren ? `
                        <button type="button" onclick="toggleAssetAccountTreeItem('asset-tree-${accountId}')" 
                                class="mr-1 text-gray-400 hover:text-gray-600 flex-shrink-0 w-4 h-4 flex items-center justify-center">
                            <svg id="asset-tree-icon-${accountId}" class="w-4 h-4 transform transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                            </svg>
                        </button>
                    ` : '<span class="mr-1 w-4 h-4 flex-shrink-0"></span>'}
                    <label class="flex items-center cursor-pointer flex-1">
                        <input type="radio" name="asset-account-radio" value="${accountId}" 
                               data-account-name="${accountName}"
                               onchange="selectAssetAccount('${accountId}', '${accountName}')"
                               class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300">
                        <span class="ml-2 text-sm text-gray-700">${accountName}</span>
                    </label>
                </div>
                ${hasChildren ? `
                    <div id="asset-tree-${accountId}" class="hidden">
                        ${renderAccountTree(account.children, level + 1)}
                    </div>
                ` : ''}
            </div>
        `;
    });
    
    return html;
}

/**
 * Load locations for the selected account
 */
async function loadLocationsForAccount(accountId, selectedLocationId = null) {
    const select = document.getElementById('asset-home-location');
    if (!select) return;
    
    try {
        const response = await fetch(`${API_BASE_URL}/locations/?account_id=${accountId}&limit=1000`, {
            headers: { 'Authorization': `Bearer ${auth.getToken()}` }
        });
        
        if (!response.ok) throw new Error('Failed to load locations');
        
        const data = await response.json();
        const locations = Array.isArray(data) ? data : (data.locations || []);
        
        // Build options
        let options = `<option value="">${t('assets.noHomeLocation')}</option>`;
        locations.forEach(loc => {
            const selected = selectedLocationId === loc.id ? 'selected' : '';
            options += `<option value="${loc.id}" ${selected}>${loc.name}</option>`;
        });
        
        select.innerHTML = options;
    } catch (error) {
        console.error('Error loading locations:', error);
        select.innerHTML = `<option value="">${t('common.error')}</option>`;
    }
}

/**
 * Load sensors for the selected account
 */
async function loadSensorsForAccount(accountId, selectedSensorIds = []) {
    const container = document.getElementById('asset-sensors-list');
    if (!container) return;
    
    try {
        // Use include_parent_sensors=true to show sensors from parent accounts
        // Sensors are infrastructure that flows down the organization hierarchy
        const response = await fetch(`${API_BASE_URL}/sensors?account_id=${accountId}&include_parent_sensors=true&limit=1000`, {
            headers: { 'Authorization': `Bearer ${auth.getToken()}` }
        });
        
        if (!response.ok) throw new Error('Failed to load sensors');
        
        const data = await response.json();
        const sensors = data.sensors || [];
        
        if (sensors.length === 0) {
            container.innerHTML = '<div class="text-gray-500 text-sm">No sensors available</div>';
            return;
        }
        
        // Build checkboxes
        let html = '<div class="space-y-2">';
        sensors.forEach(sensor => {
            const checked = selectedSensorIds.includes(sensor.id) ? 'checked' : '';
            html += `
                <label class="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input type="checkbox" value="${sensor.id}" ${checked}
                           class="asset-sensor-checkbox h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                    <span class="ml-2 text-sm text-gray-700">${sensor.name}</span>
                    ${sensor.sensor_type ? `<span class="ml-2 text-xs text-gray-500">(${sensor.sensor_type})</span>` : ''}
                </label>
            `;
        });
        html += '</div>';
        
        container.innerHTML = html;

        // Hook filter onto the search input
        const searchInput = document.getElementById('asset-sensor-search');
        if (searchInput) {
            searchInput.value = '';
            searchInput.oninput = () => {
                const query = searchInput.value.trim().toLowerCase();
                container.querySelectorAll('label').forEach(label => {
                    const text = label.innerText.toLowerCase();
                    label.style.setProperty('display', text.includes(query) ? '' : 'none', 'important');
                });
            };
        }
    } catch (error) {
        console.error('Error loading sensors:', error);
        container.innerHTML = '<div class="text-red-500 text-sm">Error loading sensors</div>';
    }
}

/**
 * Save asset (create or update)
 */
async function saveAsset(isEdit) {
    const submitBtn = document.getElementById('asset-submit-btn');
    submitBtn.disabled = true;
    submitBtn.textContent = t('common.saving');
    
    try {
        const assetId = document.getElementById('asset-asset-id').value.trim();
        const name = document.getElementById('asset-name').value.trim();
        const accountId = document.getElementById('asset-account').value;
        const homeLocationId = document.getElementById('asset-home-location').value || null;
        
        // Get selected sensors
        const sensorCheckboxes = document.querySelectorAll('.asset-sensor-checkbox:checked');
        const sensorIds = Array.from(sensorCheckboxes).map(cb => cb.value);
        
        if (!assetId) {
            showError(t('assets.assetIdRequired'));
            return;
        }
        
        if (!name) {
            showError(t('assets.nameRequired'));
            return;
        }
        
        if (!accountId) {
            showError(t('assets.accountRequired'));
            return;
        }
        
        let payload;
        let response;
        
        if (isEdit) {
            // For updates, include asset_id (it can be changed)
            payload = {
                asset_id: assetId,
                name,
                home_location_id: homeLocationId,
                sensor_ids: sensorIds
            };
            
            const assetDbId = document.getElementById('asset-id').value;
            response = await fetch(`${API_BASE_URL}/assets/${assetDbId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${auth.getToken()}`
                },
                body: JSON.stringify(payload)
            });
        } else {
            // For creation, include account_id and asset_id
            payload = {
                asset_id: assetId,
                name,
                account_id: accountId,
                home_location_id: homeLocationId,
                sensor_ids: sensorIds
            };
            
            response = await fetch(`${API_BASE_URL}/assets/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${auth.getToken()}`
                },
                body: JSON.stringify(payload)
            });
        }
        
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to save asset');
        }
        
        showSuccess(isEdit ? t('assets.updateSuccess') : t('assets.createSuccess'));
        closeModal();
        await loadAssets();
        
    } catch (error) {
        console.error('Error saving asset:', error);
        showError(error.message);
    } finally {
        submitBtn.disabled = false;
        submitBtn.textContent = isEdit ? t('common.save') : t('common.create');
    }
}

/**
 * Delete asset
 */
export async function deleteAsset(assetId, assetName) {
    const confirmed = await showConfirmDialog({
        title: t('assets.deleteAsset'),
        message: t('assets.deleteConfirm', { name: assetName }),
        confirmText: t('common.delete'),
        cancelText: t('common.cancel'),
        confirmClass: 'bg-red-600 hover:bg-red-500',
        icon: 'danger'
    });
    
    if (!confirmed) {
        return;
    }
    
    try {
        const response = await fetch(`${API_BASE_URL}/assets/${assetId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${auth.getToken()}` }
        });
        
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to delete asset');
        }
        
        showSuccess(t('assets.deleteSuccess'));
        await loadAssets();
        
    } catch (error) {
        console.error('Error deleting asset:', error);
        showError(error.message);
    }
}

// Expose functions to window for onclick handlers
window.showAssetModal = showAssetModal;
window.deleteAsset = deleteAsset;
window.selectAssetAccount = (accountId, accountName) => {
    document.getElementById('asset-account').value = accountId;
    // Trigger change for location/sensor loading
    document.getElementById('asset-account').dispatchEvent(new Event('change'));
};
window.toggleAssetAccountTreeItem = (id) => {
    const element = document.getElementById(id);
    const icon = document.getElementById(id.replace('asset-tree-', 'asset-tree-icon-'));
    if (element && icon) {
        element.classList.toggle('hidden');
        icon.classList.toggle('rotate-90');
    }
};
